<template>
    <div class="pagination">
        <button class="button button--secondary next-page button--pagination" @click="previousPage" v-if="store.currentPage !== 0">‹</button>
        <div class="pagination__numbers">
            <button class="button button--secondary button--pagination" @click="goToPage(1)" :disabled="store.currentPage === 0">1</button>
            <span class="ellipsis" v-if="ellipsisPresence.first">...</span>
            <button class="button button--secondary button--pagination" v-for="page in pagesToShow" :key="page" @click="goToPage(page)"
                :disabled="page === store.currentPage + 1">
                {{ page }}
            </button>
            <span class="ellipsis" v-if="ellipsisPresence.last">...</span>
            <button v-if="totalPages > 1" class="button button--secondary button--pagination" @click="goToPage(totalPages)"
                :disabled="store.currentPage + 1 === totalPages">{{ totalPages }}</button>
        </div>        
        <button class="button button--secondary next-page button--pagination" @click="nextPage" v-if="store.currentPage + 1 !== totalPages">›</button>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from "@/stores/store";

const props = withDefaults(defineProps<{
    pageWindowSize?: number;
}>(), {
    pageWindowSize: 7,
});

const store = useStore();

const totalPages = computed(() => {
    return Math.ceil((store.searchResult?.results.length ?? 0) / store.itemsPerPage);
});

const pageWindowSize = computed(() => {
    return Math.max(props.pageWindowSize, 1);
});

const pagesToShow = computed(() => {
    const pages: number[] = [];
    const adjustedCurrentPage = store.currentPage + 1;

    if (totalPages.value <= 1) {
        return pages;
    }

    if (totalPages.value <= pageWindowSize.value + 1) {
        for (let i = 2; i < totalPages.value; i++) {
            pages.push(i);
        }

        return pages;
    }

    const halfWindow = Math.floor(pageWindowSize.value / 2);
    const maxWindowStart = totalPages.value - pageWindowSize.value;
    const windowStart = Math.min(Math.max(adjustedCurrentPage - halfWindow, 1), maxWindowStart);
    const windowEnd = windowStart + pageWindowSize.value - 1;

    for (let i = windowStart; i <= windowEnd; i++) {
        if (i !== 1 && i !== totalPages.value) {
            pages.push(i);
        }
    }

    return pages;
});

const ellipsisPresence = computed(() => {
    if (totalPages.value <= pageWindowSize.value + 1) {
        return {
            first: false,
            last: false
        }
    }

    const firstVisiblePage = pagesToShow.value[0] ?? 1;
    const lastVisiblePage = pagesToShow.value[pagesToShow.value.length - 1] ?? 1;

    return {
        first: firstVisiblePage > 2,
        last: lastVisiblePage < totalPages.value - 1
    };
});

const firstPage = () => {
    store.currentPage = 0;
};

const nextPage = () => {
    if (store.currentPage < totalPages.value - 1) {
        store.currentPage++;
    }
};

const lastPage = () => {
    store.currentPage = totalPages.value - 1;
};

const previousPage = () => {
    if (store.currentPage > 0) {
        store.currentPage--;
    }
};

const goToPage = (page: number) => {
    store.currentPage = page - 1;
};
</script>
